
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

const colVariants = {
  hidden: { opacity: 0, x: -24 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const colRightVariants = {
  hidden: { opacity: 0, x: 24 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const listContainerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

const listItemVariants = {
  hidden: { opacity: 0, x: 18, scale: 0.98 },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

export default function AboutSection() {
  const { t } = useTranslation();

  const principles = [
    { icon: 'ri-shield-check-line', title: t('about.principles.reliability.title'), desc: t('about.principles.reliability.desc') },
    { icon: 'ri-chat-3-line', title: t('about.principles.communication.title'), desc: t('about.principles.communication.desc') },
    { icon: 'ri-list-check-3', title: t('about.principles.execution.title'), desc: t('about.principles.execution.desc') },
    { icon: 'ri-focus-3-line', title: t('about.principles.business.title'), desc: t('about.principles.business.desc') },
  ];

  return (
    <section id="about" className="py-32 lg:py-40 bg-[#1a1d23]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 lg:gap-20">
          {/* Left Column */}
          <motion.div
            variants={colVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            className="lg:col-span-2"
          >
            <div className="inline-block mb-5 px-5 py-2 rounded-full bg-[rgba(0,212,255,0.15)] backdrop-blur-sm border border-[rgba(0,212,255,0.3)]">
              <span className="text-[#00d4ff] text-xs font-semibold uppercase tracking-[2px] whitespace-nowrap">
                {t('about.label')}
              </span>
            </div>
            <h2 className="text-white font-bold text-4xl md:text-5xl leading-tight mb-8">
              {t('about.title')}
            </h2>
            <p className="text-[rgba(255,255,255,0.7)] text-lg leading-relaxed">
              {t('about.description')}
            </p>
          </motion.div>

          {/* Right Column - Principles */}
          <motion.div
            className="lg:col-span-3"
            variants={listContainerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.08 }}
          >
            <div className="flex flex-col gap-4">
              {principles.map((principle, index) => (
                <motion.div
                  key={index}
                  variants={listItemVariants}
                  whileHover={{ x: 4, transition: { duration: 0.15, ease: 'easeOut' } }}
                  className="p-6 rounded-xl bg-[rgba(255,255,255,0.06)] backdrop-blur-sm border-l-[3px] border-[#00d4ff] hover:bg-[rgba(255,255,255,0.08)] transition-colors duration-200 cursor-pointer"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                      <i className={`${principle.icon} text-[#00d4ff] text-2xl`}></i>
                    </div>
                    <div>
                      <h3 className="text-white text-lg font-semibold mb-2">{principle.title}</h3>
                      <p className="text-[rgba(255,255,255,0.6)] text-sm leading-relaxed">{principle.desc}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
